import { getUserSpaceList } from '@/services/user';
import type { FormProps } from 'antd';
import { Form, Select } from 'antd';
import { useEffect, useState } from 'react';
import { useUserInfoContext } from '@/contexts/user-info-context';
import {
  getThreadList,
  migrateThreads,
  PostMigrateThreadResponse,
} from '@/services/thread';
import { toast } from 'sonner';
import { Modal } from '../antd-wrapper/modal/global/modal';
import { useI18n } from '@/hooks/use-i18n';

interface ConvMigrateModalProps {
  open: boolean;
  onClose: () => void;
  threadId: string;
  onConfirm: (result: PostMigrateThreadResponse) => void;
}

type FieldType = {
  threads?: string[];
  spaceId?: string;
};

const ConvMigrateModal = ({
  open,
  onClose,
  threadId,
  onConfirm,
}: ConvMigrateModalProps) => {
  const { t, currentLanguage } = useI18n('components');
  const [form] = Form.useForm();
  const { userInfo } = useUserInfoContext();
  const [spaceListLoading, setSpaceListLoading] = useState(false);
  const [spaceList, setSpaceList] = useState<
    { label: string; value: string }[]
  >([]);
  const [threadListLoading, setThreadListLoading] = useState(false);
  const [threadList, setThreadList] = useState<
    { label: string; value: string }[]
  >([]);

  const fetchSpaceList = async () => {
    try {
      setSpaceListLoading(true);
      const spaces = await getUserSpaceList();
      // 过滤掉当前团队
      const filteredSpaces = (spaces || [])
        .filter((space) => space?.id !== userInfo?.active_space?.id)
        .map((space) => ({
          label: space?.space_name,
          value: space?.id,
        }));
      setSpaceList(filteredSpaces);
    } catch (error) {
      console.error('获取用户团队列表失败:', error);
    } finally {
      setSpaceListLoading(false);
    }
  };
  const fetchThreadList = async () => {
    try {
      setThreadListLoading(true);
      const threadData = (await getThreadList()) || [];
      const threadListData = threadData
        .filter((thread) => thread.state !== 'running')
        .map((thread) => ({
          label: thread.name,
          value: thread.thread_id,
        }));
      setThreadList(threadListData);
      // 如果存在threadId，自动回显当前会话
      if (threadId && threadData.length > 0) {
        const currentThread = threadData.find(
          (thread) => thread.thread_id === threadId,
        );
        if (currentThread) {
          form.setFieldsValue({
            threads: [threadId],
          });
        }
      }
    } catch (error) {
      console.log('获取线程列表失败:', error);
    } finally {
      setThreadListLoading(false);
    }
  };
  const onFinish: FormProps<FieldType>['onFinish'] = async (values) => {
    try {
      const { threads, spaceId } = values;
      const result = (await migrateThreads(
        spaceId,
        threads || [],
      )) as PostMigrateThreadResponse;

      const successCount = result?.success?.length || 0;
      const failedCount = result?.failed?.length || 0;
      if (!result) return;
      // 根据结果显示不同的提示信息
      if (failedCount === 0) {
        // 全部成功
        toast.success(
          successCount === 1
            ? t('sidebar.convMigrate.migrateSuccess')
            : t('sidebar.convMigrate.migrateAllSuccess'),
        );
      } else if (successCount === 0) {
        // 全部失败
        toast.error(
          failedCount === 1
            ? t('sidebar.convMigrate.migrateFailed')
            : t('sidebar.convMigrate.migrateAllFailed'),
        );
      } else {
        // 部分成功部分失败
        toast.success(
          t('sidebar.convMigrate.migratePartialSuccess', {
            successCount,
            failedCount,
          }),
        );
      }
      onClose?.();
      onConfirm?.(result);
    } catch (error) {
      console.error('获取线程列表失败:', error);
    } finally {
      setThreadListLoading(false);
    }
  };

  const onFinishFailed: FormProps<FieldType>['onFinishFailed'] = (
    errorInfo,
  ) => {
    console.log('Failed:', errorInfo);
  };

  // 获取用户空间列表
  useEffect(() => {
    if (!open) {
      return;
    }
    fetchThreadList();
    fetchSpaceList();
  }, [open]);
  const selectStyle =
    '[&_.ant-select-selection-item]:text-[14px] [&_.ant-select-selection-placeholder]:text-[14px] [&_.ant-select-selector]:text-[14px]';
  return (
    <Modal
      onOk={() => {
        form.submit();
      }}
      open={open}
      onCancel={onClose}
      title={t('sidebar.convMigrate.title')}
      width={628}
      height={418}
      okButtonProps={{
        className: 'w-[80px] !ml-[16px]',
      }}
      cancelButtonProps={{
        className: 'w-[80px]',
      }}
    >
      <div className="px-6 pb-[40px] mt-[8px]">
        <div className="mb-[14px] text-[#00000073] text-[14px] leading-6">
          {t('sidebar.convMigrate.description')}
        </div>
        <Form
          name="conv-migrate"
          form={form}
          initialValues={{ remember: true }}
          autoComplete="off"
          labelAlign="left"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          labelCol={{
            style: {
              width: currentLanguage === 'zh-CN' ? '80px' : '165px',
            },
          }}
          wrapperCol={{ style: { marginLeft: '0px' } }}
          requiredMark={false}
          className="[&_.ant-form-item]:!mb-4 [&_.ant-form-item:last-child]:!mb-0"
        >
          <Form.Item<FieldType>
            label={t('sidebar.convMigrate.selectedConversations')}
            name="threads"
            rules={[
              {
                required: true,
                message: t('sidebar.convMigrate.selectConversationRequired'),
              },
            ]}
          >
            <Select
              mode="multiple"
              placeholder={t(
                'sidebar.convMigrate.selectConversationPlaceholder',
              )}
              options={threadList}
              maxTagCount="responsive"
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              loading={threadListLoading}
              className={selectStyle}
            />
          </Form.Item>

          <Form.Item label={t('sidebar.convMigrate.currentTeam')}>
            <span className="text-[14px] text-[#000000BF]">
              {userInfo?.active_space?.space_name || '-'}
            </span>
          </Form.Item>

          <Form.Item<FieldType>
            label={t('sidebar.convMigrate.migrateTo')}
            name="spaceId"
            rules={[
              {
                required: true,
                message: t('sidebar.convMigrate.selectTeamRequired'),
              },
            ]}
          >
            <Select
              placeholder={t('sidebar.convMigrate.selectTeamPlaceholder')}
              options={spaceList}
              showSearch={false}
              loading={spaceListLoading}
              className={selectStyle}
            />
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default ConvMigrateModal;
